import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PageLayout from 'layouts/PageLayout';
import PageContentLayout from 'layouts/PageContentLayout';
import TitleAndActionSummaryBar from 'components/SummaryBars/TitleAndActionSummaryBar';
import { GetBillingGroupById } from 'services/BillingGroupsService';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import Alert from 'components/Alert';
import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Permission } from 'common/models/Permissions';
import { userHasSinglePermission } from 'util/PermissionsUtils';
import { AddRecentRecord } from 'util/LocalStorageUtils';
import useRecordHotkeys from 'hooks/UseRecordHotkeys';
import { BillingGroup } from '../../common/models/BillingGroup';
import ContactsGridComponent from '../../components/Grids/ContactsGrid';
import ViewRecordScreenLayout from 'components/ScreenLayouts/BillingGroups/ViewRecordScreenLayout';
import { MenuOptionDefinition } from '../../common/models/MenuDefinition';
import Box from '@mui/material/Box';
import ActionMenu from '../../components/Menus/ActionMenu';
import ManageAdditionalContactsDialog from '../../components/Dialogs/BillingGroups/ManageAdditionalContactsDialog';

const summaryLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'none' };

const formatDateString = (d: string, f: string = 'DD MMM YYYY h:mm A') => {
  if (d && d !== '0001-01-01T00:00:00') {
    const m = moment(d);
    if (m.isValid()) return m.format(f);
  }
  return 'Never';
};

const renderSummary = (c: BillingGroup) => {
  const tooltipContent = (
    <table>
      <tbody>
        <tr>
          <Typography variant="caption" fontWeight={600} component="td">
            ID
          </Typography>
          <Typography variant="caption" pl="10px" component="td">
            {c.id}
          </Typography>
        </tr>
        <tr>
          <Typography
            variant="caption"
            fontWeight={600}
            component="td"
            sx={{ verticalAlign: 'top' }}
          >
            Name
          </Typography>
          <Typography variant="caption" pl="10px" component="td">
            {c.name}
          </Typography>
        </tr>
        <tr>
          <Typography variant="caption" fontWeight={600} component="td">
            Created Date
          </Typography>
          <Typography variant="caption" pl="10px" component="td">
            {formatDateString(c.createdDate)}
          </Typography>
        </tr>
        <tr>
          <Typography variant="caption" fontWeight={600} component="td">
            Created By
          </Typography>
          <Typography variant="caption" pl="10px" component="td">
            {c.createdByName}
          </Typography>
        </tr>
      </tbody>
    </table>
  );

  const jobSummary = (
    <Tooltip arrow title={tooltipContent}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {c.name}
      </span>
    </Tooltip>
  );

  return <>{jobSummary}</>;
};

export default function BillingGroupRecord() {
  const [billingGroup, setBillingGroup] = useState<BillingGroup>();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDefaultTab, setIsFetchingDefaultTab] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoadingTab, setIsLoadingTab] = useState(false);
  const [isFinishedSetup, setIsFinishedSetup] = useState(false);
  const [showManageAdditionalContacts, setShowManageAdditionalContacts] = useState(false);
  const [activeTab, setActiveTab] = useState('Home');
  const [searchParams] = useSearchParams();

  const isFinancialController = useMemo(
    () => userHasSinglePermission(Permission.FinancialController),
    []
  );

  const navigate = useNavigate();

  const billingGroupId = useMemo(() => {
    const parsedId = +(params.id ?? '0');
    if (isNaN(parsedId)) return 0;
    return parsedId;
  }, [params.id]);

  const hotkeySettings = useMemo<Record<string, () => void>>(() => {
    return {
      e: () => navigate(`/billing-groups/${billingGroupId}/edit`),
    };
  }, [billingGroupId, navigate]);

  useRecordHotkeys(hotkeySettings, !isFinancialController);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const res = await GetBillingGroupById(billingGroupId);
      if (res === null) navigate('/not-found');
      if (res) {
        AddRecentRecord(21, billingGroupId, res.name);
        setBillingGroup(res);
      }
      setIsLoading(false);
    };
    getData();
  }, [billingGroupId, navigate]);

  const actionMenuDefinitions = useMemo<MenuOptionDefinition[]>(() => {
    const actions: MenuOptionDefinition[] = [
      {
        label: 'Manage Additional Contacts',
        type: 'action',
        action: () => setShowManageAdditionalContacts(true),
        allow: () => isFinancialController,
      },
    ];

    return actions;
  }, [isFinancialController]);

  const SummaryBar = useMemo(() => {
    if (billingGroup) {
      const action1 = isFinancialController ? (
        <Link key="editLink" to={`/billing-groups/${billingGroupId}/edit`} style={summaryLinkStyle}>
          <Button variant="contained" color="success" sx={{ mr: '5px' }}>
            Edit
          </Button>
        </Link>
      ) : (
        <React.Fragment key="editPlaceholder" />
      );

      const action = (
        <Box key="actionsMenu">
          <ActionMenu definition={actionMenuDefinitions} />
        </Box>
      );

      return (
        <TitleAndActionSummaryBar
          height="4.5rem"
          title={billingGroup ? renderSummary(billingGroup) : ''}
          action={[action1, action]}
          browserTabTitle={!billingGroup ? '' : billingGroup.name + ' - Billing Groups'}
        />
      );
    }
  }, [billingGroupId, billingGroup, isFinancialController]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const getData = async () => {
      setIsFetchingDefaultTab(true);
      if (tabParam) {
        setActiveTab(tabParam);
      }
      setIsFetchingDefaultTab(false);
      setIsFinishedSetup(true);
    };
    getData();
  }, [searchParams]);

  const handleTabChange = useCallback(
    (e: React.SyntheticEvent, newValue: string) => {
      navigate(`/billing-groups/${billingGroupId}?tab=${newValue}`);
    },
    [billingGroupId, navigate]
  );

  return (
    <>
      <Snackbar
        open={successMessage !== ''}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={errorMessage !== ''} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <ManageAdditionalContactsDialog
        open={showManageAdditionalContacts}
        billingGroupId={billingGroupId}
        closeHandler={() => setShowManageAdditionalContacts(false)}
        loadingHandler={setIsLoading}
        errorHandler={setErrorMessage}
        successHandler={setSuccessMessage}
      />
      <PageLayout paddingTop={0} SummaryBar={SummaryBar}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            value="Home"
            label={
              <>
                Home
                <br />
                &nbsp;
              </>
            }
          />
          <Tab
            value="Contacts"
            label={
              <>
                Additional Contacts
                <br />
                &nbsp;
              </>
            }
          />
        </Tabs>
        <PageContentLayout showLoading={isLoading || isFetchingDefaultTab || isLoadingTab}>
          {isFinishedSetup && activeTab === 'Home' && (
            <ViewRecordScreenLayout
              billingGroup={billingGroup ? billingGroup : null}
              loadingHandler={setIsLoadingTab}
              errorHandler={setErrorMessage}
            />
          )}
          {isFinishedSetup && activeTab === 'Contacts' && (
            <ContactsGridComponent
              source="billing-group-record"
              sourceId={billingGroupId}
              loadingHandler={setIsLoadingTab}
              errorHandler={setErrorMessage}
              successHandler={setSuccessMessage}
              gridName="BillingGroup/Contacts"
            />
          )}
        </PageContentLayout>
      </PageLayout>
    </>
  );
}
